/**
 * ErrorBoundary Component
 *
 * A reusable React Error Boundary that catches JavaScript errors in child components,
 * logs them using the existing logger, and displays a fallback UI.
 *
 * Features:
 * - Catches and logs errors with full stack traces
 * - Integrates with the existing logging system
 * - Customizable fallback UI
 * - Automatic error reporting to logger
 * - Support for custom error handlers
 */

import React, { Component, ReactNode, ErrorInfo } from 'react';
import logger from '../utils/logger';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, errorInfo: ErrorInfo, reset: () => void) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
  level?: 'app' | 'route' | 'component';
}

interface BuildInfo {
  hash: string;
  timestamp: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  buildInfo: BuildInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
    buildInfo: null,
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
  }

  componentDidMount() {
    // Fetch build info
    fetch('/build-info.json')
      .then(res => res.json())
      .then(data => this.setState({ buildInfo: data }))
      .catch(() => {
        // In development, build-info.json might not exist
        this.setState({ buildInfo: { hash: 'dev', timestamp: new Date().toISOString() } });
      });
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, level = 'component' } = this.props;

    // Log the error using the existing logger
    logger.error('React Error Boundary caught an error', {
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack,
      },
      errorInfo: {
        componentStack: errorInfo.componentStack,
      },
      level,
      timestamp: new Date().toISOString(),
    }, 'error-boundary');

    // Store error info in state
    this.setState({
      errorInfo,
    });

    // Call custom error handler if provided
    if (onError) {
      try {
        onError(error, errorInfo);
      } catch (handlerError) {
        logger.error('Error in custom error handler', {
          error: handlerError,
        }, 'error-boundary');
      }
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeysChanged = resetKeys.some(
        (key, index) => key !== prevProps.resetKeys![index]
      );

      if (hasResetKeysChanged) {
        this.reset();
      }
    }
  }

  reset = (): void => {
    logger.info('Error boundary reset', {}, 'error-boundary');
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  formatBuildInfo = (): string => {
    const { buildInfo } = this.state;
    if (!buildInfo) return 'Unknown';

    const date = new Date(buildInfo.timestamp);
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    return `${buildInfo.hash.substring(0, 7)} (${formattedDate} ${formattedTime})`;
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, level = 'component' } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback(error, errorInfo!, this.reset);
        }
        return fallback;
      }

      // Default fallback UI based on error level
      return this.renderDefaultFallback(level, error, errorInfo);
    }

    return children;
  }

  private renderDefaultFallback(level: string, error: Error, errorInfo: ErrorInfo | null) {
    const isDevelopment = process.env.NODE_ENV === 'development';

    return (
      <div className={`error-boundary-fallback error-boundary-${level}`}>
        <div className="error-boundary-content">
          <div className="error-boundary-icon">⚠️</div>
          <h2 className="error-boundary-title">
            {level === 'app' ? 'Application Error' :
             level === 'route' ? 'Page Error' :
             'Something went wrong'}
          </h2>
          <p className="error-boundary-message">
            {level === 'app'
              ? "We're sorry, but something went wrong. Please refresh the page to continue."
              : level === 'route'
              ? "This page encountered an error. You can try going back or refreshing."
              : "This component encountered an error. You can try again."}
          </p>

          <div className="error-boundary-actions">
            <button
              onClick={this.reset}
              className="error-boundary-button error-boundary-button-primary"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="error-boundary-button error-boundary-button-secondary"
            >
              Go Home
            </button>
            {level !== 'app' && (
              <button
                onClick={() => window.location.reload()}
                className="error-boundary-button error-boundary-button-secondary"
              >
                Refresh Page
              </button>
            )}
          </div>

          {isDevelopment && (
            <details className="error-boundary-details">
              <summary className="error-boundary-details-summary">
                Error Details (Development Only)
              </summary>
              <div className="error-boundary-details-content">
                <div className="error-boundary-error-info">
                  <h3>Build Version</h3>
                  <pre>{this.formatBuildInfo()}</pre>

                  <h3>Error Message</h3>
                  <pre>{error.message}</pre>

                  <h3>Stack Trace</h3>
                  <pre>{error.stack}</pre>

                  {errorInfo && (
                    <>
                      <h3>Component Stack</h3>
                      <pre>{errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </div>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
